// Jelszó-csere kártya (POST /profile/password) — jelenlegi + új + megerősítés, KLIENS-oldali policy-tükör
// (min. 12 kar., kis+nagy+szám, új≠régi) + a szerver 422-violation kódjainak lokalizált leképezése. SIKER →
// az Odin MINDEN refresh-tokent visszavon (más eszközökön kiléptet) → a UI erre FIGYELMEZTET. Külön mentés-gomb.
import { PasswordChangeBodySchema } from '@atlas/contracts';
import { KeyIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { PasswordField } from '@/components/shared/form-fields';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { ImpersonationFence } from '@/features/impersonation/components/impersonation-fence';
import {
  PasswordRequirements,
  type PasswordRequirement,
  type RequirementState,
} from '@/features/profile/components/password-requirements';
import { ProfileSection } from '@/features/profile/components/profile-section';
import {
  checkPasswordPolicy,
  EMPTY_PASSWORD_FORM,
  // A SZERVER 422-kódjaihoz kell (az visszaadhat olyat is, amit a kliens-lista nem fed — pl. `too_long`).
  passwordViolationMessage,
  PASSWORD_MIN_LENGTH,
  type PasswordFormValues,
  type PasswordViolationCode,
} from '@/features/profile/form';
import { api } from '@/lib/api';

interface PolicyErrorBody {
  violations?: { path: string; code: string }[];
}

export function PasswordCard(): JSX.Element {
  const { t } = useTranslation();
  const form = useForm<PasswordFormValues>({
    // Élő validálás a repo bevett módján: a mezőből kilépve (blur) validál, utána gépelés közben is frissül —
    // NEM minden leütésre kezdettől (az „kiabálna", mielőtt befejezné). Ugyanez a mód az oldal másik formján
    // (`profile-details-form.tsx`) és ~10 sheeten.
    mode: 'onTouched',
    defaultValues: EMPTY_PASSWORD_FORM,
  });
  const mutation = api.profileChangePassword.useMutation();
  // A követelmény-lista a beküldési kísérlet UTÁN vált „hibáztató" (piros) megjelenésre — addig semleges.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const onSubmit = (values: PasswordFormValues): void => {
    form.clearErrors();
    // KLIENS-oldali kapuk (a szerver a forrás-igazság): kötelező jelenlegi jelszó + megerősítés-egyezés +
    // policy-tükör. MINDEGYIK a MEZŐN jelenik meg — ez a felhasználó egyetlen visszajelzése arról, mi a baj.
    if (values.currentPassword.trim() === '') {
      form.setError('currentPassword', { message: t('validation:profile.password.required') });
      return;
    }
    if (values.newPassword !== values.confirmPassword) {
      form.setError('confirmPassword', { message: t('validation:profile.password.mismatch') });
      return;
    }
    // A policy-szabályokat a KÖVETELMÉNY-LISTA jeleníti meg (a `submitAttempted` már true → a hiányzók
    // pirosra váltanak). Külön mező-hibát NEM írunk ki: az szó szerint megismételné a lista tartalmát.
    if (checkPasswordPolicy(values.newPassword, values.currentPassword).length > 0) {
      form.setFocus('newPassword');
      return;
    }
    // Contract-alak (min(1) mindkettőn) — a részletes policyt a szerver érvényesíti.
    const parsed = PasswordChangeBodySchema.safeParse({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
    if (!parsed.success) {
      form.setError('newPassword', { message: t('validation:profile.password.generic') });
      return;
    }

    mutation.mutate(
      { body: parsed.data },
      {
        onSuccess: (res) => {
          if (res.status === 200) {
            form.reset(EMPTY_PASSWORD_FORM);
            toast.success(t('toast:profile.passwordChanged'));
            // A siker MINDEN refresh-tokent visszavon → más eszközökön kiléptet. Figyelmeztetünk (tartós toast).
            toast.warning(t('toast:profile.passwordRevokeWarning'), { duration: 8000 });
          }
        },
        onError: (err) => {
          const status = (err as { status?: number } | null)?.status;
          const body = (err as { body?: PolicyErrorBody } | null)?.body;
          if (status === 422 && body?.violations && body.violations.length > 0) {
            for (const v of body.violations) {
              form.setError('newPassword', { message: passwordViolationMessage(v.code, t) });
            }
            return;
          }
          if (status === 403) {
            form.setError('currentPassword', {
              message: t('validation:profile.password.wrongCurrent'),
            });
            return;
          }
          if (status === 429) {
            toast.error(t('toast:profile.rateLimited'));
            return;
          }
          toast.error(t('toast:profile.saveError'));
        },
      },
    );
  };

  // A mentés-gomb SZÁNDÉKOSAN NINCS a validáláshoz kötve (csak a folyamatban lévő mentés tiltja).
  //
  // MIÉRT (élesben megfogott hiba): korábban a `disabled` a policy-ellenőrzésen állt, a policy hibaüzenetei
  // viszont KIZÁRÓLAG az `onSubmit`-ben keletkeznek — amit csak ez a gomb indít. Így pontosan akkor volt
  // elérhetetlen a magyarázat, amikor kellett volna: a felhasználó annyit látott, hogy „a gomb mindig szürke",
  // és semmi nem mondta meg, MELYIK szabály sérül (a leggyakoribb: az új jelszó = a jelenlegi). Egy magyarázat
  // nélkül letiltott gomb amúgy is a11y-anti-minta. A beküldés most lefut, a policy-állapotot pedig a
  // KÖVETELMÉNY-LISTA mutatja élőben — ugyanaz a minta, mint a repo többi űrlapján (a submit sehol sincs
  // `isValid`-hez kötve).

  // Az élő követelmény-állapot. A `watch()` a KONKRÉT mezőkre iratkozik fel (nem a teljes formra) → csak
  // ezek változására rendereljük újra a listát.
  const newPassword = form.watch('newPassword');
  const currentPassword = form.watch('currentPassword');
  const violations = checkPasswordPolicy(newPassword, currentPassword);
  /**
   * Egy megjelenített követelmény TÖBB policy-kódot is lefedhet (a „kis- és nagybetű" kettőt) — akkor
   * teljesült, ha EGYIK sem sérül. Így 4 szabály marad → kiegyensúlyozott 2×2 rács, nincs árva ötödik elem;
   * és a felhasználónak sem kell két külön sort összeolvasnia ugyanarról a dologról.
   */
  const ruleState = (...codes: PasswordViolationCode[]): RequirementState => {
    // Üres új jelszónál MÉG NINCS mit értékelni → minden szabály semleges. E nélkül az „eltér a jelenlegitől"
    // már betöltéskor zölden állna (a `checkPasswordPolicy` üres jelszónál nem jelzi az egyezést), miközben a
    // többi semleges — ez félrevezető „majdnem kész" képet adna.
    if (newPassword === '') return submitAttempted ? 'failed' : 'pending';
    if (!codes.some((c) => violations.includes(c))) return 'met';
    return submitAttempted ? 'failed' : 'pending';
  };
  const requirements: PasswordRequirement[] = [
    {
      key: 'length',
      label: t('profile.password.rule.length', { count: PASSWORD_MIN_LENGTH }),
      state: ruleState('too_short'),
    },
    {
      key: 'case',
      label: t('profile.password.rule.case'),
      state: ruleState('missing_lowercase', 'missing_uppercase'),
    },
    { key: 'digit', label: t('profile.password.rule.digit'), state: ruleState('missing_digit') },
    {
      key: 'different',
      label: t('profile.password.rule.different'),
      state: ruleState('same_as_current'),
    },
  ];

  return (
    <ProfileSection
      title={t('profile.password.title')}
      description={t('profile.password.policyHint', { count: PASSWORD_MIN_LENGTH })}
    >
      {/* MEGSZEMÉLYESÍTÉS-FAL: az Odin a `/iam/me/password`-öt `@DenyImpersonation()`-nel zárja (403
          `impersonation_fenced`), tehát más nevében a csere NEM megy — a felület ezt KIMONDJA, és nem
          engedi kitölteni az űrlapot (user-lelet, 2026-09-09: eddig csak a mentésnél derült ki). */}
      {/* `hint={false}`: a magyarázat a lap tetején, az „Alapadatok" fölött áll (egy lapon egyszer). */}
      <ImpersonationFence hint={false}>
        <Form {...form}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <PasswordField<PasswordFormValues>
                name="currentPassword"
                label={t('profile.password.current')}
                autoComplete="current-password"
                required
              />
            </div>
            <PasswordField<PasswordFormValues>
              name="newPassword"
              label={t('profile.password.new')}
              autoComplete="new-password"
              required
            />
            <PasswordField<PasswordFormValues>
              name="confirmPassword"
              label={t('profile.password.confirm')}
              autoComplete="new-password"
              required
            />
            {/* Az élő követelmény-lista a két új-jelszó mező ALATT, teljes szélességben — így egyszerre látszik
              a beírt jelszóval, és nem tolja szét a mező-rácsot. */}
            <div className="sm:col-span-2">
              <PasswordRequirements requirements={requirements} />
            </div>
          </div>
          <div className="mt-4 flex">
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => {
                setSubmitAttempted(true);
                void form.handleSubmit(onSubmit)();
              }}
            >
              <KeyIcon />
              {mutation.isPending ? t('profile.password.saving') : t('profile.password.submit')}
            </Button>
          </div>
        </Form>
      </ImpersonationFence>
    </ProfileSection>
  );
}
